import { FC } from "react";
import { Button, Group, Modal, NumberInput, Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { gameSetClock } from "../../../openapi/api-client";

interface SetClockModalProps {
  gameId: string;
  currentTime: number;
  onClose: () => void;
}

export const SetClockModal: FC<SetClockModalProps> = ({
  gameId,
  currentTime,
  onClose,
}) => {
  const queryClient = useQueryClient();

  const currentMinutes = Math.floor(currentTime / 60);
  const currentSeconds = Math.floor(currentTime % 60);

  const form = useForm<{
    minutes: number;
    seconds: number;
  }>({
    initialValues: {
      minutes: currentMinutes,
      seconds: currentSeconds,
    },
    validate: {
      minutes: (value) => (value < 0 ? "Minuten müssen positiv sein" : null),
      seconds: (value) =>
        value < 0 || value > 59
          ? "Sekunden müssen zwischen 0 und 59 liegen"
          : null,
    },
  });

  const setClockMutation = useMutation({
    mutationFn: gameSetClock,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["game", { gameId }] });
      onClose();
    },
  });

  const handleSubmit = form.onSubmit(async (values) => {
    const time = values.minutes * 60 + values.seconds;
    await setClockMutation.mutateAsync({
      id: gameId,
      time,
    });
  });

  return (
    <Modal opened={true} onClose={onClose} title="Uhr anpassen" size="sm">
      <form onSubmit={handleSubmit}>
        <Stack>
          <Group grow>
            <NumberInput
              label="Minuten"
              min={0}
              max={99}
              {...form.getInputProps("minutes")}
            />
            <NumberInput
              label="Sekunden"
              min={0}
              max={59}
              {...form.getInputProps("seconds")}
            />
          </Group>

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit" loading={setClockMutation.isPending}>
              Uhr setzen
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
